/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation, SerializedStyles } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import type Maybe from 'graphql/tsutils/Maybe';

import { Grid, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { GridProps, Theme } from '@coursera/cds-core';

import type { EntWebsiteBlockAssembly } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import { EntWebsiteDomainContext } from 'bundles/ent-website/components/EntWebsiteDomainContext';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { MENU_HEIGHT, MENU_HEIGHT_MOBILE, NAV_BREAKPOINT } from 'bundles/ent-website/components/EntWebsitePage';
import { getMetaHeight } from 'bundles/ent-website/components/MetaNavigation';
import { MAX_PAGE_CONTAINER_WIDTH } from 'bundles/ent-website/constants/layout';
import parseLayoutOptions from 'bundles/ent-website/utils/parseLayoutOptions';

type GridSizeProps = {
  lg?: GridProps['lg'];
  md?: GridProps['md'];
  sm?: GridProps['sm'];
  xl?: GridProps['xl'];
  xs?: GridProps['xs'];
};

type GridSize = NonNullable<Exclude<GridProps['lg'], boolean>>;

// shortcut type
export type BlockAssembly = Pick<
  EntWebsiteBlockAssembly,
  'anchorId' | 'slug' | 'theme' | 'layout' | 'layoutOptions' | 'mobileBreakpoint'
>;

type Props = {
  // Any block assembly properties that can be trusted can be attached here
  assembly: BlockAssembly;
  // End of assembly overrides
  renderImage?: () => React.ReactNode;
  renderContent: () => React.ReactNode;
  alignItems?: GridProps['alignItems'];
  blockStyle?: SerializedStyles | ((theme: Theme) => SerializedStyles);
  panelStyle?: SerializedStyles | ((theme: Theme) => SerializedStyles);
  fullWidth?: boolean;
};

export type ButtonVariants = 'primary' | 'secondary' | 'ghost';

// handy shortcut for overriding layout with 'no-image'
export function noImage(assembly: BlockAssembly) {
  return { ...assembly, layout: 'no-image' };
}

// handy shortcut for overriding layoutOptions with 'no-y-padding'
export function noYPadding(assembly: BlockAssembly) {
  return { ...assembly, layoutOptions: [...(assembly.layoutOptions ?? []), 'no-y-padding'] };
}

// handy shortcut for overriding layoutOptions with 'top-image-on-phone'
export function topImageOnPhone(assembly: BlockAssembly) {
  return {
    ...assembly,
    layoutOptions: [...(assembly.layoutOptions ?? []), 'top-image-on-phone'],
  };
}

// Some layouts look bad when rendering desktop size on xs resolution only.
// Block Layout provides an option to render mobile on below md instead of below sm
export const getGridSizeProps = (
  layout: Maybe<string>,
  useMdBreakpoint = false
): { contentGridSizeProps: GridSizeProps; imageGridSizeProps: GridSizeProps } => {
  const getDesktopGridItemSize = (): { image: GridSize; content: GridSize } => {
    switch (layout) {
      case 'hero':
      case 'image50/text50':
      case 'text50/image50':
        return { image: 6, content: 6 };
      case 'image40/text60':
      case 'text60/image40':
        return { image: 5, content: 7 };
      case 'image60/text40':
      case 'text40/image60':
        return { image: 7, content: 5 };
      case 'image30/text70':
      case 'text70/image30':
        return { image: 4, content: 8 };
      case 'image70/text30':
      case 'text30/image70':
        return { image: 8, content: 4 };
      default:
        return { image: 6, content: 6 };
    }
  };

  const mobileSize: GridSizeProps = { [useMdBreakpoint ? 'sm' : 'xs']: 12 };

  const desktopGridSize = getDesktopGridItemSize();
  const desktopContentSize: GridSizeProps = { [useMdBreakpoint ? 'md' : 'sm']: desktopGridSize.content };
  const desktopImageSize: GridSizeProps = { [useMdBreakpoint ? 'md' : 'sm']: desktopGridSize.image };

  const contentGridSizeProps: GridSizeProps = { ...mobileSize, ...desktopContentSize };
  const imageGridSizeProps: GridSizeProps = { ...mobileSize, ...desktopImageSize };

  return { contentGridSizeProps, imageGridSizeProps };
};

function BlockLayout({
  renderContent,
  renderImage,
  alignItems,
  assembly,
  fullWidth = false,
  blockStyle,
  panelStyle,
}: Props) {
  const isMobileBreakpoint = useMediaQuery(() => breakpoints.down('xs'));
  const useMdBreakpoint = assembly?.mobileBreakpoint === 'md';

  const options = parseLayoutOptions(assembly.layoutOptions);
  const { domain } = useContext(EntWebsiteDomainContext);

  const styles = {
    block: css`
      padding-top: var(--cds-spacing-800);
      padding-bottom: var(--cds-spacing-800);
    `,

    // no-y-padding is a special layout option that allows the block to be flush with the previous block
    blockNoYPadding: css`
      /* stylelint-disable-line no-empty-source */
    `,

    // this wrapper is required to allow the BlockLayout correctly "goes beneath" the previous Proof points.
    wrapper: css`
      width: 100%;
      display: flex;
      justify-content: center;
    `,

    container: css`
      max-width: ${fullWidth ? 'unset' : `${breakpoints.values[MAX_PAGE_CONTAINER_WIDTH]}px`};
    `,

    centerOnMobile: css`
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      ${breakpoints.down('xs')} {
        align-items: center;
      }
    `,

    centerAlways: css`
      display: flex;
      flex-direction: column;
      align-items: center;
    `,

    panel: css`
      position: relative;
      padding-left: var(--cds-spacing-800);
      padding-right: var(--cds-spacing-800);

      ${breakpoints.down('sm')} {
        padding-left: var(--cds-spacing-400);
        padding-right: var(--cds-spacing-400);
      }

      ${breakpoints.down('xs')} {
        padding-left: var(--cds-spacing-300);
        padding-right: var(--cds-spacing-300);
      }
    `,

    panelEmbedded: css`
      position: relative;
    `,

    heroImage: css`
      position: relative;
      margin-top: calc(var(--cds-spacing-800) * -1);
      margin-bottom: calc(var(--cds-spacing-800) * -1);
      height: calc(100% + 128px);
      padding-left: 0;
      padding-right: 0;
      overflow: hidden;

      ${breakpoints.down('xs')} {
        margin: 0 calc(var(--cds-spacing-200) * -1) calc(var(--cds-spacing-800) * -1);
        height: calc(320px + 64px);
      }
    `,

    imageContainerRHS: css`
      text-align: right;
    `,

    // base overrides for all themes
    // `.rc-RichText a p` is to override the HowModule.subCtaText
    themeBase: css`
      .rc-RichText a p {
        color: var(--cds-color-blue-700);
      }
    `,

    themeInvertFg: css`
      color: var(--cds-color-white-0);

      .rc-Markdown.styled a {
        color: var(--cds-color-white-0);
      }

      .rc-RichText a p {
        color: var(--cds-color-white-0);
      }
    `,

    themeWhiteBg: css`
      background-color: var(--cds-color-white-0);
    `,

    themeGreyBg: css`
      background-color: var(--cds-color-grey-25);
    `,

    themeLightBlueBg: css`
      background: var(--ui-neutral-background-bg-light-bg-blue-100, #ebf3ff);
    `,

    themeBlueBg: css`
      background-color: var(--cds-color-blue-700);
    `,

    // #002859 is the brand standard dark blue.
    // Hopefully someday the CDS blue[900] can be updated to the same value.
    themeDarkBlueBg: css`
      background-color: #002859;
    `,

    themeBlackBg: css`
      background-color: var(--cds-color-grey-975);
    `,

    hideOnPhone: css`
      ${breakpoints.down('xs')} {
        display: none;
      }
    `,
    ghostAnchor: css`
      position: relative;
      width: 1px;
      height: 1px;

      /* Offset the margin top */
      top: calc(-${MENU_HEIGHT} - ${getMetaHeight({ domain })} + 1px);
      margin-top: -1px;

      ${breakpoints.down(NAV_BREAKPOINT)} {
        /* Offset the margin top */
        top: calc(-${MENU_HEIGHT_MOBILE} - ${getMetaHeight({ domain })} + 1px);
      }
    `,
  };

  // TODO: need a way to restrict the values of theme name
  // currently theme name is a list in the Contentful
  const themeMapping: Record<string, Interpolation<Theme>> = {
    'white-bg': [styles.themeBase, styles.themeWhiteBg],
    'grey-bg': [styles.themeBase, styles.themeGreyBg],
    'light-blue-bg': [styles.themeBase, styles.themeLightBlueBg],
    'blue-bg-invert-fg': [styles.themeBase, styles.themeBlueBg, styles.themeInvertFg],
    'dark-blue-bg-invert-fg': [styles.themeBase, styles.themeDarkBlueBg, styles.themeInvertFg],
    'black-bg': [styles.themeBase, styles.themeBlackBg, styles.themeInvertFg],
  };

  // reverse layout to make sure images always appear at the bottom on small screens
  let isReversed = ['image50/text50', 'image30/text70', 'image70/text30'].includes(assembly.layout ?? '');
  if (options.topImageOnPhone) {
    isReversed = !isReversed;
  }

  const stylesPanel = options.innerLayout ? styles.panelEmbedded : styles.panel;
  const stylesImage = options.hero ? styles.heroImage : stylesPanel;
  const imageContainerAlignment = ['text50/image50', 'text70/image30', 'text30/image70'].includes(assembly.layout ?? '')
    ? styles.imageContainerRHS
    : undefined;

  const renderLayoutContent = (imageGrid: React.ReactNode, contentGrid: React.ReactNode) => {
    return options.topImageOnPhone ? (
      <>
        {isMobileBreakpoint && options?.hideMediaOnPhone ? undefined : imageGrid}
        {isMobileBreakpoint && options?.hideContentOnPhone ? undefined : contentGrid}
      </>
    ) : (
      <>
        {isMobileBreakpoint && options?.hideContentOnPhone ? undefined : contentGrid}
        {isMobileBreakpoint && options?.hideMediaOnPhone ? undefined : imageGrid}
      </>
    );
  };

  const renderLayout = () => {
    const { contentGridSizeProps, imageGridSizeProps } = getGridSizeProps(assembly?.layout, useMdBreakpoint);

    switch (assembly.layout) {
      case 'no-image':
        return (
          <Grid className="BlockLayout-contentGrid" item xs={12} css={[stylesPanel, panelStyle]}>
            {renderContent()}
          </Grid>
        );

      // deprecated: use `layoutOptions: ['hero']` instead
      case 'hero': {
        const contentGrid = (
          <Grid item className="BlockLayout-contentGrid" {...contentGridSizeProps} css={[stylesPanel, panelStyle]}>
            {renderContent()}
          </Grid>
        );

        const imageGrid = (
          <Grid item className="BlockLayout-imageGrid" {...imageGridSizeProps} css={[styles.heroImage]}>
            {renderImage?.()}
          </Grid>
        );

        return renderLayoutContent(imageGrid, contentGrid);
      }

      case 'image50/text50':
      case 'text50/image50': {
        const contentGrid = (
          <Grid item className="BlockLayout-contentGrid" {...contentGridSizeProps} css={[stylesPanel, panelStyle]}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid
            item
            className="BlockLayout-imageGrid"
            {...imageGridSizeProps}
            css={[stylesImage, imageContainerAlignment]}
          >
            {renderImage?.()}
          </Grid>
        );

        return renderLayoutContent(imageGrid, contentGrid);
      }

      case 'image40/text60':
      case 'text60/image40': {
        const contentGrid = (
          <Grid item className="BlockLayout-contentGrid" {...contentGridSizeProps} css={[stylesPanel, panelStyle]}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid
            item
            className="BlockLayout-imageGrid"
            {...imageGridSizeProps}
            css={[stylesImage, imageContainerAlignment]}
          >
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case 'image60/text40':
      case 'text40/image60': {
        const contentGrid = (
          <Grid className="BlockLayout-contentGrid" {...contentGridSizeProps} css={[stylesPanel, panelStyle]}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid
            className="BlockLayout-imageGrid"
            item
            {...imageGridSizeProps}
            css={[stylesImage, imageContainerAlignment]}
          >
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case 'image30/text70':
      case 'text70/image30': {
        const contentGrid = (
          <Grid item className="BlockLayout-contentGrid" {...contentGridSizeProps} css={[stylesPanel, panelStyle]}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid
            item
            className="BlockLayout-imageGrid"
            {...imageGridSizeProps}
            css={[stylesImage, imageContainerAlignment]}
          >
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case 'image70/text30':
      case 'text30/image70': {
        const contentGrid = (
          <Grid item className="BlockLayout-contentGrid" {...contentGridSizeProps} css={[stylesPanel, panelStyle]}>
            {renderContent()}
          </Grid>
        );
        const imageGrid = (
          <Grid
            item
            className="BlockLayout-imageGrid"
            {...imageGridSizeProps}
            css={[stylesImage, imageContainerAlignment]}
          >
            {renderImage?.()}
          </Grid>
        );
        return renderLayoutContent(imageGrid, contentGrid);
      }

      case 'image-top/text-bottom':
        return (
          <>
            <Grid item xs={12} css={[stylesImage, imageContainerAlignment]}>
              {renderImage?.()}
            </Grid>
            <Grid item xs={12} css={[stylesPanel, panelStyle]}>
              {renderContent()}
            </Grid>
          </>
        );

      case 'text-top/image-bottom':
        return (
          <>
            <Grid item xs={12} css={[stylesPanel, panelStyle]}>
              {renderContent()}
            </Grid>
            <Grid item xs={12} css={[stylesImage, imageContainerAlignment]}>
              {renderImage?.()}
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  function ghostAnchor() {
    // This component is invisible but puts itself a variable number of pixels
    // above the block component for visually accurate anchoring.
    const { anchorId, slug } = assembly;
    const id = anchorId || slug || '';

    return <div data-testid={id} id={id} css={styles.ghostAnchor} />;
  }

  return options.innerLayout ? (
    <>
      {ghostAnchor()}
      <Grid
        data-testid="block_layout"
        container
        alignItems="center"
        direction={isReversed ? 'row-reverse' : 'row'}
        css={options.hideOnPhone ? styles.hideOnPhone : null}
      >
        {renderLayout()}
      </Grid>
    </>
  ) : (
    <>
      {ghostAnchor()}
      <div
        className="BlockLayout_Container"
        css={[
          options.noYPadding ? styles.blockNoYPadding : styles.block,
          assembly.theme ? themeMapping[assembly.theme] : null,
          options.hideOnPhone ? styles.hideOnPhone : null,
          blockStyle,
        ]}
      >
        <div css={styles.wrapper}>
          <Grid
            data-testid="block_layout"
            container
            alignItems={alignItems ?? 'flex-start'}
            justifyContent="center"
            direction={isReversed ? 'row-reverse' : 'row'}
            css={styles.container}
          >
            {renderLayout()}
          </Grid>
        </div>
      </div>
    </>
  );
}

export default BlockLayout;
